import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Send, X, MessageCircle, Phone, Mail, Loader2, Mic } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useAIAgent } from '../contexts/AIAgentContext';
import { PackageCards } from './PackageCards';
import { VoiceAgentTestWidget } from './VoiceAgentTestWidget';

const WHATSAPP_NUMBER = '447436466686'; // +44 7436 466686
const SUGGESTED_PROMPTS = {
  homepage: [
    '5-night Umrah package price',
    'Best 4-star hotel near Haram',
    'Visa requirements for UK travellers',
  ],
  umrah: [
    'Which tier suits a family of 4?',
    'Difference between Silver and Gold?',
    'Recommend hotels for elderly parents',
  ],
  hotel: [
    'Hotels under £150 per night',
    'Closest 4-star to the Haram',
    'Family room options',
  ],
  checkout: [
    'What is your cancellation policy?',
    'Is my booking ATOL-protected?',
    'How do I get the e-ticket?',
  ],
};

const formatReply = (text) => {
  // Render simple markdown-ish content: bullets and bold
  if (!text) return null;
  const lines = String(text).split(/\r?\n/);
  return lines.map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <div key={i} className="h-2" />;
    if (/^[-*•]\s+/.test(trimmed)) {
      return (
        <div key={i} className="flex gap-2 text-sm leading-relaxed">
          <span className="text-emerald-600 mt-0.5">•</span>
          <span>{trimmed.replace(/^[-*•]\s+/, '')}</span>
        </div>
      );
    }
    return (
      <p key={i} className="text-sm leading-relaxed">
        {trimmed}
      </p>
    );
  });
};

export const AIChatWidget = () => {
  const {
    isOpen, openAgent, closeAgent, toggleAgent,
    context, messages, pending, sendMessage, submitLead, leadSignal, unread,
  } = useAIAgent();

  const [input, setInput] = useState('');
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showVoice, setShowVoice] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [leadStatus, setLeadStatus] = useState(null); // 'success' | 'error' | null
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pending, isOpen]);

  useEffect(() => {
    if (leadSignal === 'hot') setShowLeadForm(true);
  }, [leadSignal]);

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (!input.trim() || pending) return;
    sendMessage(input.trim());
    setInput('');
  };

  const handleQuickPrompt = (p) => {
    if (pending) return;
    sendMessage(p);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadForm.email && !leadForm.phone) {
      setLeadStatus('error');
      return;
    }
    const r = await submitLead(leadForm);
    setLeadStatus(r.success ? 'success' : 'error');
    if (r.success) {
      setShowLeadForm(false);
      setLeadForm({ name: '', email: '', phone: '', message: '' });
    }
  };

  const ctxLabel = {
    homepage: 'Travel Concierge',
    umrah: 'Umrah Builder Co-pilot',
    hotel: 'Hotel Specialist',
    checkout: 'Checkout Assistant',
  }[context] || 'Travel Concierge';

  return (
    <>
      {/* Floating launcher button */}
      {!isOpen && (
        <button
          type="button"
          onClick={() => openAgent()}
          data-testid="ai-agent-launcher"
          aria-label="Open AI travel concierge"
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[60] group flex items-center gap-2 bg-gradient-to-br from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white rounded-full shadow-xl shadow-emerald-500/40 px-4 py-3 transition-all active:scale-95"
        >
          <div className="relative">
            <Sparkles className="h-5 w-5" />
            {unread > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                {unread}
              </span>
            )}
          </div>
          <span className="hidden sm:inline font-semibold text-sm">Ask Ami</span>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div
          data-testid="ai-agent-panel"
          className="fixed inset-x-2 bottom-2 top-16 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:top-auto sm:w-[400px] sm:h-[600px] z-[60] flex flex-col bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 px-4 py-3 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">Ami · {ctxLabel}</div>
                <div className="text-[11px] text-white/80">Powered by GPT-5.2</div>
              </div>
            </div>
            <button
              type="button"
              data-testid="voice-agent-launch"
              onClick={() => setShowVoice(true)}
              className="p-1.5 hover:bg-white/15 rounded-lg transition"
              aria-label="Voice call"
              title="Try the AI voice agent"
            >
              <Mic className="h-4 w-4" />
            </button>
            <button
              type="button"
              data-testid="ai-agent-close"
              onClick={closeAgent}
              className="p-1.5 hover:bg-white/15 rounded-lg transition"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-gradient-to-b from-emerald-50/40 to-white"
          >
            {messages.map((m, i) => (
              <div key={i} className="space-y-2">
                <div className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 shadow-sm ${
                      m.role === 'user'
                        ? 'bg-emerald-600 text-white rounded-br-md'
                        : 'bg-white text-slate-800 border border-slate-200 rounded-bl-md'
                    }`}
                  >
                    {m.role === 'user' ? (
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.content}</p>
                    ) : (
                      <div className="space-y-1">{formatReply(m.content)}</div>
                    )}
                  </div>
                </div>
                {m.role === 'assistant' && m.packages && (
                  <PackageCards
                    data={m.packages}
                    onSelect={(pkg) => {
                      // Tell Ami the user picked a tier — keeps the conversation flowing.
                      sendMessage(`I'd like to go with the ${pkg.label} package (£${pkg.total_price_gbp}). How do I book it?`);
                    }}
                  />
                )}
              </div>
            ))}
            {pending && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-3.5 py-2.5 shadow-sm flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-600" />
                  <span className="text-xs text-slate-500">Ami is thinking…</span>
                </div>
              </div>
            )}

            {/* Hot lead inline form */}
            {showLeadForm && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
                <div className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5" />
                  Want a human agent to call you back?
                </div>
                <form onSubmit={handleLeadSubmit} className="space-y-2">
                  <Input
                    placeholder="Your name"
                    value={leadForm.name}
                    data-testid="ai-lead-name"
                    onChange={(e) => setLeadForm((f) => ({ ...f, name: e.target.value }))}
                    className="h-8 text-sm"
                  />
                  <Input
                    placeholder="Phone (WhatsApp preferred)"
                    value={leadForm.phone}
                    data-testid="ai-lead-phone"
                    onChange={(e) => setLeadForm((f) => ({ ...f, phone: e.target.value }))}
                    className="h-8 text-sm"
                  />
                  <Input
                    placeholder="Email (optional)"
                    type="email"
                    value={leadForm.email}
                    data-testid="ai-lead-email"
                    onChange={(e) => setLeadForm((f) => ({ ...f, email: e.target.value }))}
                    className="h-8 text-sm"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      size="sm"
                      data-testid="ai-lead-submit"
                      className="flex-1 h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
                    >
                      Request call back
                    </Button>
                    <a
                      href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                        'Hi Flight380, I was chatting with Ami and would like to speak to your team about an Umrah package.'
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="ai-lead-whatsapp"
                      className="inline-flex items-center justify-center h-8 px-3 rounded-md bg-green-500 hover:bg-green-600 text-white text-xs font-semibold"
                    >
                      WhatsApp
                    </a>
                  </div>
                  {leadStatus === 'success' && (
                    <p className="text-[11px] text-emerald-700">Thanks! Our team will be in touch shortly.</p>
                  )}
                  {leadStatus === 'error' && (
                    <p className="text-[11px] text-red-600">Please enter your email or phone.</p>
                  )}
                </form>
              </div>
            )}
          </div>

          {/* Quick prompts */}
          {messages.length <= 2 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5 bg-white">
              <a
                href="/umrah/quote"
                data-testid="ai-open-quote-form"
                className="text-[11px] bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white font-medium rounded-full px-3 py-1 transition shadow-sm"
              >
                ✨ Build an Umrah package
              </a>
              {(SUGGESTED_PROMPTS[context] || []).map((p, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleQuickPrompt(p)}
                  data-testid={`ai-prompt-${i}`}
                  className="text-[11px] bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-full px-2.5 py-1 transition"
                >
                  {p}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form onSubmit={handleSubmit} className="border-t border-slate-200 bg-white px-3 py-2.5 flex items-end gap-2">
            <Textarea
              data-testid="ai-agent-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              rows={1}
              placeholder="Ask about Umrah packages, hotels, visas…"
              className="resize-none min-h-[36px] max-h-24 text-sm border-slate-300 focus-visible:ring-emerald-500"
              disabled={pending}
            />
            <Button
              type="submit"
              data-testid="ai-agent-send"
              size="icon"
              disabled={!input.trim() || pending}
              className="h-9 w-9 bg-emerald-600 hover:bg-emerald-700 flex-shrink-0"
            >
              {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        </div>
      )}

      {showVoice && (
        <VoiceAgentTestWidget onClose={() => setShowVoice(false)} />
      )}

    </>
  );
};

export default AIChatWidget;
